export type LogoutReason = 'user' | 'session_expired';

type InFlightRef = { current: boolean };
type ProjectClearOptions = { discardPendingChanges?: boolean };

export const AUTH_STORAGE_KEYS = [
  'bark_auth_token',
  'bark_user_data',
  'bark_refresh_token',
  'bark_chats',
  'bark_active_chat_id',
  'bark_regulatory_settings',
] as const;

export function shouldClearAuthAfterRefreshFailure(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { requiresLogin?: unknown; status?: unknown };
  return candidate.requiresLogin === true
    || candidate.status === 401
    || candidate.status === 403;
}

export type LogoutTransitionDependencies = {
  inFlightRef: InFlightRef;
  reason: LogoutReason;
  warningAlreadyShown?: boolean;
  clearActiveProject: (options?: ProjectClearOptions) => Promise<boolean>;
  showWarning: (message: string) => void;
  saveUserState: () => void;
  remoteLogout: () => Promise<unknown>;
  clearReactState: () => void;
  clearPlanReloadTimer: () => void;
  resetEphemeralState: () => void;
  storage: Pick<Storage, 'removeItem'>;
  navigateHome: () => void;
  warn?: (message: string) => void;
};

const unsavedProjectWarning = (reason: LogoutReason): string => (
  reason === 'session_expired'
    ? 'Your session expired before the current project could be saved. Unsaved changes were discarded.'
    : 'You were logged out, but the current project could not be saved.'
);

/**
 * Complete an ordinary logout even when a project save or the remote logout
 * request fails. Keeping this boundary outside App makes the credential-cleanup
 * invariant directly testable without rendering the full application shell.
 */
export async function runLogoutTransition(
  dependencies: LogoutTransitionDependencies,
): Promise<boolean> {
  const {
    inFlightRef,
    reason,
    warningAlreadyShown = false,
    clearActiveProject,
    showWarning,
    saveUserState,
    remoteLogout,
    clearReactState,
    clearPlanReloadTimer,
    resetEphemeralState,
    storage,
    navigateHome,
    warn = console.warn,
  } = dependencies;

  if (inFlightRef.current) return false;
  inFlightRef.current = true;

  try {
    let projectSaved: boolean;
    try {
      projectSaved = await clearActiveProject();
    } catch (error) {
      if (!warningAlreadyShown) showWarning(unsavedProjectWarning(reason));
      throw error;
    }

    if (!projectSaved) {
      if (!warningAlreadyShown) showWarning(unsavedProjectWarning(reason));
      await clearActiveProject({ discardPendingChanges: true });
    }

    saveUserState();
    await remoteLogout();
  } catch {
    warn('Logout preparation failed, continuing with local logout');
  } finally {
    try { clearReactState(); }
    catch { warn('React logout cleanup failed'); }

    try { clearPlanReloadTimer(); }
    catch { warn('Logout timer cleanup failed'); }

    try { resetEphemeralState(); }
    catch { warn('Ephemeral logout cleanup failed'); }

    for (const key of AUTH_STORAGE_KEYS) {
      try { storage.removeItem(key); }
      catch { warn(`Logout storage cleanup failed for ${key}`); }
    }

    try { navigateHome(); }
    catch { warn('Logout navigation failed'); }

    inFlightRef.current = false;
  }

  return true;
}
